import React from 'react';
import { Box, Paper, IconButton, Tooltip, Typography, Chip, Divider, useMediaQuery } from '@mui/material';
import { Edit, PushPinOutlined } from '@mui/icons-material';
import { useUnpinAnnouncementMutation } from '../announcementApiSlice';
import toast from 'react-hot-toast';
import UserAvatar from '../../../common/UserAvatar';
import { formatFullDateTime } from '../../../utils/dateUtils';
import { severityColor, severityIcon } from '../utility/utilities';
import { useSelector } from 'react-redux';
import { selectCurrentUserEmpCode } from '../../Auth/authSlice';
import { animateScroll as scroll, scroller } from 'react-scroll';
import { useTheme } from '@emotion/react';
import { variants } from '../../../variants/variants';

const PinnedAnnouncement = ({ pinnedAnnouncement, user, setEditingAnnouncement }) => {
  const userEmpCode = useSelector(selectCurrentUserEmpCode);
  const [unpinAnnouncement] = useUnpinAnnouncementMutation();
  const theme = useTheme();
  const handleUnpin = async () => {
    // await toast.promise(unpinAnnouncement(pinnedAnnouncement._id), {
    //   loading: 'Unpinning announcement...',
    //   success: 'Announcement unpinned successfully!',
    //   error: err => err.data?.message || 'Error unpinning announcement. Please try again.',
    // });
    await unpinAnnouncement(pinnedAnnouncement._id);
  };
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const handleEdit = () => {
    setEditingAnnouncement({
      announcementId: pinnedAnnouncement._id,
      announcementText: pinnedAnnouncement.announcementText,
      severity: pinnedAnnouncement.severity,
      category: pinnedAnnouncement.category,
      attachments: pinnedAnnouncement.attachments,
    });
  };

  const scrollToAnnouncement = () => {
    scroller.scrollTo(`announcement-${pinnedAnnouncement._id}`, {
      duration: 800,
      delay: 0,
      smooth: 'easeInOutQuart',
      containerId: 'announcement-list-container', // Ensure the correct container is targeted
    });
  };

  if (!pinnedAnnouncement) return null;
  const isOwn = pinnedAnnouncement.authorEmpCode === userEmpCode || false;

  const truncateHTML = (html, maxHeight) => {
    const element = document.createElement('div');
    element.innerHTML = html;
    const clone = element.cloneNode(true);

    while (clone.scrollHeight > maxHeight && clone.childNodes.length) {
      clone.removeChild(clone.lastChild);
    }

    if (clone.innerHTML !== element.innerHTML) {
      clone.innerHTML += '...';
    }

    return clone.innerHTML;
  };

  return (
    <Paper
      sx={{
        position: 'sticky',
        top: 0,
        backgroundColor: '#fefefe',
        zIndex: 1000,
        p: 1,
        boxShadow: 4,
        borderRadius: 3,

        display: 'flex',
        alignItems: 'center',
        borderLeft: `5px solid ${severityColor(pinnedAnnouncement.severity)}`,
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          boxShadow: 6,
        },
        cursor: 'default',
      }}
      onClick={() => scrollToAnnouncement()}
    >
      <UserAvatar
        showStatus={false}
        src={pinnedAnnouncement.authorUser?.photo}
        sx={{ height: { xs: 35, md: 40 }, width: { xs: 35, md: 40 }, marginRight: 1 }}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', border: 0, flex: 0.7 }}>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            [theme.breakpoints.down('lg')]: {
              pt: 0,
            },
            ...(isMobile && {
              flexDirection: 'column',
               ...(!isMobile && {
              alignItems: 'center',
            }),
            }),
          }}
        >
          <Typography
            noWrap
            variant="body1"
            fontWeight="bold"
            sx={{ display: 'inline-block', textOverflow: 'ellipsis', maxWidth: '300px', fontSize: variants.BODY }}
          >
            {pinnedAnnouncement.authorUser?.username}
          </Typography>
          {isMobile && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{
                fontSize: '0.60rem',  
              }}
            >
              {formatFullDateTime(pinnedAnnouncement.datePosted)}
            </Typography>
          )}
          {!isMobile && <Divider orientation="vertical" flexItem sx={{ mx: 1, height: '24px' }} />}
          {/* <Chip
            label={pinnedAnnouncement.severity}
            sx={{
              backgroundColor: severityColor(pinnedAnnouncement.severity),
              color: 'white',
              height: '20px',
              fontSize: '0.75rem',
              mr: 2,
            }}
          /> */}
          <IconButton
            disabled
            disableRipple
            sx={{
              position: 'absolute',
              bottom: 2,
              right: 0,
              [theme.breakpoints.down('lg')]: {
                display: 'none',
              },
            }}
          >
            {severityIcon(pinnedAnnouncement.severity)}
          </IconButton>
          {!isMobile && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{
                whiteSpace: 'nowrap',
              }}
            >
              {formatFullDateTime(pinnedAnnouncement.datePosted)}
            </Typography>
          )}
        </Box>
        {/* <Typography
          noWrap
          variant="subtitle2"
          sx={{ display: 'inline-block', textOverflow: 'ellipsis', maxWidth: isOwn ? '60vw' : '65vw' }}
          dangerouslySetInnerHTML={{ __html: pinnedAnnouncement.announcementText }}
        /> */}
        <Typography
          noWrap
          variant="subtitle2"
          sx={{
            display: 'inline-block',
            textOverflow: 'ellipsis',
            maxWidth: isOwn ? '60vw' : '65vw',
            maxHeight: '20px',
            overflow: 'hidden',
            whiteSpace: 'normal',
          }}
          dangerouslySetInnerHTML={{ __html: truncateHTML(pinnedAnnouncement.announcementText, 30) }}
        />
      </Box>
      {isOwn ? (
        <Box sx={{ border: 0, position: 'absolute', bottom: isMobile ? 2 : 25, right: isMobile ? 2 : 8 }}>
          <Tooltip title="Unpin">
            <IconButton onClick={handleUnpin} size="small">
              <PushPinOutlined color="primary" sx={{ fontSize: isMobile ? '15px' : '18px', cursor: 'default' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton onClick={handleEdit} size="small">
              <Edit color="primary" sx={{ fontSize: isMobile ? '15px' : '18px', cursor: 'default' }} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : null}
    </Paper>
  );
};

export default PinnedAnnouncement;
